import React, { useState, useEffect } from 'react';
import {
    Book, BarChart, Search, Add, Edit, Sort,
    Delete, School, People, Menu
} from '@mui/icons-material';
import Box from '@mui/material/Box';
import { Typography, Chip, Button } from '@mui/material';
import CreateCourseDialog from '../components/CreateCourseDialog';
import DeleteConfirmationDialog from '../components/DeleteConfirmationDialog'
import { useAuth0 } from '@auth0/auth0-react'
import { useOrgUser } from '../context/OrgUserProvider'
import { deleteObject, putObject } from '../api/s3Api';
import CategoryManager from '../components/CategoryManager';

// Public bucket URLs
const PUBLIC_BUCKET_BASE_URL = 'https://qallix-public.s3.ap-southeast-1.amazonaws.com';
const COURSES_MANIFEST_URL = `${PUBLIC_BUCKET_BASE_URL}/QallixAcademy/SoftwareCourses/course-manifest.json`;

// Sidebar Component 
const Sidebar = ({ activeTab, setActiveTab }) => {
    return (
        <Box sx={{
            width: 220,
            backgroundColor: 'white',
            display: 'flex',
            flexDirection: 'column',
            height: '80vh',
            borderRadius: '10px',
            margin: '20px 0 20px 20px',
            position: 'sticky',
            fontFamily: "'Satoshi', sans-serif",
        }}>
            <Box sx={{ flex: 1 }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '12px 20px',
                        cursor: 'pointer',
                        color: activeTab === 'courses' ? 'white' : 'black',
                        transition: 'background-color 0.2s',
                        backgroundColor: activeTab === 'courses' ? '#372e5eff' : 'transparent',
                        '&:hover': { backgroundColor: '#282241ff', color: 'white' },
                        borderTopLeftRadius: '6px',
                        borderTopRightRadius: '6px',
                        fontFamily: "'Satoshi', sans-serif",
                    }}
                    onClick={() => setActiveTab('courses')}
                >
                    <Book sx={{ marginRight: '15px', fontSize: '18px' }} />
                    <span style={{ fontFamily: "'Satoshi', sans-serif" }}>Courses</span>
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '12px 20px',
                        cursor: 'pointer',
                        transition: 'background-color 0.2s',
                        color: activeTab === 'courses' ? 'black' : 'white',
                        backgroundColor: activeTab === 'analytics' ? '#372e5eff' : 'transparent',
                        '&:hover': { backgroundColor: '#282241ff', color: 'white' },
                        fontFamily: "'Satoshi', sans-serif",
                    }}
                    onClick={() => setActiveTab('analytics')}
                >
                    <BarChart sx={{ marginRight: '15px', fontSize: '16px' }} />
                    <span style={{ fontFamily: "'Satoshi', sans-serif" }}>Analytics</span>
                </Box>
            </Box>
        </Box>
    );
};

// Courses Component
const CoursesTab = () => {
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [courseToDelete, setCourseToDelete] = useState(null);
    const [thumbnails, setThumbnails] = useState({});
    const { getAccessTokenSilently } = useAuth0()
    const [categoryManagerOpen, setCategoryManagerOpen] = useState(false);
    const { orgUser } = useOrgUser()

    const handleCourseCreated = () => {
        // This will trigger the useEffect to refetch courses
        fetchCourses();
    };

    useEffect(() => {
        fetchCourses();
    }, []);

    const handleEditClick = (course) => {
        // Open course editor in a new tab
        window.open(`/manage-courses/${course.id}/course-info`);
    };

    const fetchCourses = async () => {
        try {
            setLoading(true);
            setError(null);

            // Add cache-busting parameter to prevent browser caching
            const cacheBuster = `?v=${Date.now()}`;

            // First fetch the course manifest from public bucket with cache control headers
            const manifestResponse = await fetch(COURSES_MANIFEST_URL + cacheBuster, {
                headers: {
                    'Cache-Control': 'no-cache, no-store, must-revalidate',
                    'Pragma': 'no-cache'
                }
            });
            if (!manifestResponse.ok) {
                throw new Error(`Failed to fetch course manifest: ${manifestResponse.status}`);
            }

            const manifest = await manifestResponse.json();
            const publishedCourses = manifest.courses.filter(course => !course.isDeleted);

            // Fetch course info for each published course
            const coursePromises = publishedCourses.map(async (course) => {
                try {
                    const courseInfoUrl = `${PUBLIC_BUCKET_BASE_URL}/QallixAcademy/SoftwareCourses/${course.id}/course-info.json${cacheBuster}`;
                    const courseInfoResponse = await fetch(courseInfoUrl, {
                        headers: {
                            'Cache-Control': 'no-cache, no-store, must-revalidate',
                            'Pragma': 'no-cache'
                        }
                    });

                    if (!courseInfoResponse.ok) {
                        console.warn(`Failed to fetch course info for ${course.slug}: ${courseInfoResponse.status}`);
                        return null;
                    }

                    const courseInfo = await courseInfoResponse.json();

                    // Normalize the course data to ensure consistent property names
                    const normalizedCourse = {
                        id: courseInfo.id || courseInfo.Id || course.slug,
                        title: courseInfo.title || courseInfo.Title,
                        level: courseInfo.level || courseInfo.Level,
                        slug: courseInfo.slug || courseInfo.Slug || course.slug,
                        duration: courseInfo.duration || courseInfo.Duration,
                        noOflessons: courseInfo.noOflessons || courseInfo.lessonsCount || courseInfo.LessonsCount || 0,
                        status: courseInfo.status || courseInfo.Status,
                        thumbnail: courseInfo.thumbnail || courseInfo.ImageKey || '',
                        category: courseInfo.category || courseInfo.Category || 'Uncategorized',
                        // Store the thumbnail URL for public access
                        thumbnailUrl: courseInfo.thumbnail ?
                            `${PUBLIC_BUCKET_BASE_URL}/QallixAcademy/SoftwareCourses/${course.id}/${courseInfo.thumbnail}` :
                            null
                    };

                    return normalizedCourse;
                } catch (err) {
                    console.error(`Error fetching course info for ${course.slug}:`, err);
                    return null;
                }
            });

            const allCourses = (await Promise.all(coursePromises))

            setCourses(allCourses);

            // Preload thumbnails
            await preloadThumbnails(allCourses);
        } catch (err) {
            setError(err.message);
            console.error('Error fetching courses:', err);
        } finally {
            setLoading(false);
        }
    };

    // Preload thumbnails for better user experience
    const preloadThumbnails = async (courses) => {
        const thumbnailMap = {};

        const preloadPromises = courses.map(async (course) => {
            if (course.thumbnailUrl) {
                try {
                    // Add cache busting to thumbnail URLs too
                    const cacheBuster = `?v=${Date.now()}`;
                    const thumbnailUrl = course.thumbnailUrl + cacheBuster;

                    // Create an image object to preload
                    const img = new Image();
                    img.src = thumbnailUrl;

                    // Wait for image to load or fail
                    await new Promise((resolve, reject) => {
                        img.onload = resolve;
                        img.onerror = reject;
                    });

                    thumbnailMap[course.slug] = course.thumbnailUrl;
                } catch (err) {
                    console.warn(`Failed to preload thumbnail for ${course.slug}:`, err);
                    thumbnailMap[course.slug] = null;
                }
            } else {
                thumbnailMap[course.slug] = null;
            }
        });

        await Promise.all(preloadPromises);
        setThumbnails(thumbnailMap);
    };

    const handleDeleteClick = (course) => {
        setCourseToDelete(course);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!courseToDelete) return;

        try {
            const token = await getAccessTokenSilently({
                audience: 'https://qallix-api',
            });
            const orgName = orgUser.orgName;

            // First, get the current course manifest
            const cacheBuster = `?v=${Date.now()}`;
            const manifestResponse = await fetch(COURSES_MANIFEST_URL + cacheBuster, {
                headers: {
                    'Cache-Control': 'no-cache, no-store, must-revalidate',
                    'Pragma': 'no-cache'
                }
            });

            if (!manifestResponse.ok) {
                throw new Error(`Failed to fetch course manifest: ${manifestResponse.status}`);
            }

            const manifest = await manifestResponse.json();

            // Update the manifest to mark the course as deleted
            const updatedCourses = manifest.courses.map(course =>
                course.id === courseToDelete.id ? { ...course, isDeleted: true } : course
            );

            const updatedManifest = {
                ...manifest,
                courses: updatedCourses
            };

            // Upload the updated manifest to S3
            const putData = {
                organization: orgName,
                putObjectItems: [
                    {
                        bucketName: "qallix-public",
                        contentType: "application/json",
                        fileContent: btoa(JSON.stringify(updatedManifest, null, 2)),
                        key: "QallixAcademy/SoftwareCourses/course-manifest.json",
                    }
                ]
            };

            const uploadResponse = await putObject(token, putData);

            if (uploadResponse.statusCode !== 200) {
                throw new Error("Failed to update course manifest");
            }

            // Remove the course from the local state
            setCourses(courses.filter(course => course.id !== courseToDelete.id));
            setDeleteDialogOpen(false);
            setCourseToDelete(null);

            // Show success message
            setError(null); // Clear any previous errors

        } catch (err) {
            setError(err.message);
            setDeleteDialogOpen(false);
            setCourseToDelete(null);
        }
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setCourseToDelete(null);
    };

    const handleRetry = () => {
        fetchCourses();
    };

    // Function to determine level color
    const getLevelColor = (level) => {
        switch (level) {
            case 'Basic': return '#2ecc71';
            case 'Intermediate': return '#332872';
            case 'Advanced': return '#e33e1d';
            default: return '#95a5a6';
        }
    };

    return (
        <Box sx={{ padding: '20px', fontFamily: "'Satoshi', sans-serif" }}>
            {/* Delete Confirmation Dialog */}
            <DeleteConfirmationDialog
                open={deleteDialogOpen}
                onClose={handleDeleteCancel}
                onConfirm={handleDeleteConfirm}
                courseTitle={courseToDelete?.title || ''}
            />

            {/* Add Course Dialog */}
            <CreateCourseDialog
                open={createDialogOpen}
                onClose={() => setCreateDialogOpen(false)}
                onCourseCreated={handleCourseCreated}
            />

            {/* Add the CategoryManager component */}
            <CategoryManager
                open={categoryManagerOpen}
                onClose={(success) => {
                    setCategoryManagerOpen(false);
                    if (success) {
                        // Refresh courses if needed
                        fetchCourses();
                    }
                }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', fontFamily: "'Satoshi', sans-serif" }}>
                <Box sx={{
                    position: 'relative',
                    marginBottom: '20px',
                    width: '100%',
                    maxWidth: '400px',
                    fontFamily: "'Satoshi', sans-serif",
                }}>
                    <Search sx={{
                        position: 'absolute',
                        left: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: 'grey'
                    }} />
                    <input type="text" placeholder="Search courses..." style={{
                        width: '100%',
                        padding: '10px 10px 10px 35px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '16px',
                        fontFamily: "'Satoshi', sans-serif",
                    }} />
                </Box>
                <Box sx={{ display: 'flex', gap: '10px', fontFamily: "'Satoshi', sans-serif" }}>
                    <Box
                        sx={{
                            backgroundColor: '#332872',
                            color: 'white',
                            border: 'none',
                            padding: '10px 15px',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'start',
                            fontWeight: 500,
                            '&:hover': { backgroundColor: '#2c2458ff' },
                            fontFamily: "'Satoshi', sans-serif",
                        }}
                        onClick={() => setCategoryManagerOpen(true)}
                    >
                        <Sort sx={{ marginRight: '5px' }} />
                        Manage Categories
                    </Box>
                    <Box
                        sx={{
                            backgroundColor: '#332872',
                            color: 'white',
                            border: 'none',
                            padding: '10px 15px',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'start',
                            fontWeight: 500,
                            '&:hover': { backgroundColor: '#2c2458ff' },
                            fontFamily: "'Satoshi', sans-serif",
                        }}
                        onClick={() => setCreateDialogOpen(true)}
                    >
                        <Add sx={{ marginRight: '5px' }} />
                        Add New Course
                    </Box>
                </Box>
            </Box>

            {/* Error message that doesn't block the UI */}
            {error && (
                <Box sx={{
                    padding: '10px 15px',
                    backgroundColor: '#ffebee',
                    color: '#c62828',
                    borderRadius: '4px',
                    marginBottom: '20px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    fontFamily: "'Satoshi', sans-serif",
                }}>
                    <Box sx={{ fontFamily: "'Satoshi', sans-serif" }}>
                        Error: {error}
                    </Box>
                    <Button
                        onClick={handleRetry}
                        sx={{
                            color: '#332872',
                            fontFamily: "'Satoshi', sans-serif",
                            fontWeight: 'bold',
                            '&:hover': {
                                backgroundColor: 'rgba(51, 40, 114, 0.1)'
                            }
                        }}
                    >
                        Retry
                    </Button>
                </Box>
            )}

            {loading ? (
                <Box sx={{ padding: '20px', display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px', fontFamily: "'Satoshi', sans-serif" }}>
                    <Typography sx={{ fontFamily: "'Satoshi', sans-serif" }}>Loading courses...</Typography>
                </Box>
            ) : (
                /* Table format for courses */
                <Box sx={{
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                    fontFamily: "'Satoshi', sans-serif",
                }}>
                    <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: '200px 1fr 240px 120px 120px 120px 120px',
                        padding: '15px 20px',
                        backgroundColor: '#f8f9fa',
                        fontWeight: 'bold',
                        borderBottom: '1px solid #e9ecef',
                        fontFamily: "'Satoshi', sans-serif",
                    }}>
                        <Box sx={{ fontFamily: "'Satoshi', sans-serif" }}>Thumbnail</Box>
                        <Box sx={{ fontFamily: "'Satoshi', sans-serif" }}>Title</Box>
                        <Box sx={{ fontFamily: "'Satoshi', sans-serif" }}>Slug</Box>
                        <Box sx={{ fontFamily: "'Satoshi', sans-serif" }}>Level</Box>
                        <Box sx={{ fontFamily: "'Satoshi', sans-serif" }}>Category</Box>
                        <Box sx={{ fontFamily: "'Satoshi', sans-serif" }}>Status</Box>
                        <Box sx={{ fontFamily: "'Satoshi', sans-serif" }}>Actions</Box>
                    </Box>

                    {courses.length === 0 ? (
                        <Box sx={{ padding: '20px', textAlign: 'center', fontFamily: "'Satoshi', sans-serif" }}>
                            <Typography sx={{ fontFamily: "'Satoshi', sans-serif" }}>No courses found. Add a new course to get started.</Typography>
                        </Box>
                    ) : (
                        courses.map(course => (
                            <Box key={`${course.category}-${course.slug}`} sx={{
                                display: 'grid',
                                gridTemplateColumns: '200px 1fr 240px 120px 120px 120px 120px',
                                padding: '15px 20px',
                                alignItems: 'center',
                                borderBottom: '1px solid #f2f2f2',
                                transition: 'background-color 0.2s',
                                '&:hover': { backgroundColor: '#f8f9fa' },
                                fontFamily: "'Satoshi', sans-serif",
                            }}>
                                {/* Thumbnail */}
                                <Box sx={{
                                    width: '150px',
                                    height: '90px',
                                    backgroundColor: '#ecf0f1',
                                    backgroundImage: thumbnails[course.slug]
                                        ? `url(${thumbnails[course.slug]})`
                                        : 'linear-gradient(135deg, #3498db 0%, #2c3e50 100%)',
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center',
                                    borderRadius: '4px'
                                }} />

                                {/* Title */}
                                <Box sx={{
                                    fontWeight: '500',
                                    color: '#2c3e50',
                                    fontFamily: "'Satoshi', sans-serif",
                                }}>
                                    {course.title}
                                </Box>

                                {/* Slug */}
                                <Box sx={{
                                    fontWeight: '500',
                                    color: '#2c3e50',
                                    fontFamily: "'Satoshi', sans-serif",
                                }}>
                                    {course.slug}
                                </Box>
                                {/* Level */}
                                <Box>
                                    <Chip
                                        size="small"
                                        sx={{
                                            backgroundColor: getLevelColor(course.level),
                                            color: 'white',
                                            fontFamily: "'Satoshi', sans-serif",
                                            minWidth: '25px',
                                        }}
                                    />
                                </Box>
                                {/* Category */}
                                <Box>
                                    <Chip
                                        label={course.category}
                                        size="small"
                                        sx={{
                                            backgroundColor: '#000000ff',
                                            color: 'white',
                                            fontWeight: 'bold',
                                            minWidth: '90px',
                                            fontFamily: "'Satoshi', sans-serif",
                                        }}
                                    />
                                </Box>
                                {/* Status */}
                                <Box>
                                    <Chip
                                        label={course.status || 'Draft'}
                                        size="small"
                                        sx={{
                                            backgroundColor: (course.status || 'Draft') === 'Published' ? '#3e3a79ff' : '#95a5a6',
                                            color: 'white',
                                            fontWeight: 'bold',
                                            minWidth: '80px',
                                            fontFamily: "'Satoshi', sans-serif",
                                        }}
                                    />
                                </Box>

                                {/* Actions */}
                                <Box sx={{
                                    display: 'flex',
                                    gap: '10px',
                                    fontFamily: "'Satoshi', sans-serif",
                                }}>
                                    <Box sx={{
                                        color: '#7f8c8d',
                                        cursor: 'pointer',
                                        '&:hover': { color: '#3498db' }
                                    }}
                                        onClick={() => handleEditClick(course)}
                                    >
                                        <Edit />
                                    </Box>
                                    <Box
                                        sx={{
                                            color: '#7f8c8d',
                                            cursor: 'pointer',
                                            '&:hover': { color: '#e74c3c' }
                                        }}
                                        onClick={() => handleDeleteClick(course)}
                                    >
                                        <Delete />
                                    </Box>
                                </Box>
                            </Box>
                        ))
                    )}
                </Box>
            )}
        </Box>
    );
};

// Analytics Component 
const AnalyticsTab = () => {
    return (
        <Box sx={{ padding: '20px', fontFamily: "'Satoshi', sans-serif" }}>
        </Box>
    );
};

// Main Component 
const ManageCourses = () => {
    const [activeTab, setActiveTab] = useState('courses');

    const renderTab = () => {
        switch (activeTab) {
            case 'courses':
                return <CoursesTab />;
            case 'analytics':
                return <AnalyticsTab />;
            default:
                return <CoursesTab />;
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', fontFamily: "'Satoshi', sans-serif" }}>
            <Box sx={{
                display: 'flex',
                flex: 1,
                backgroundColor: '#f5f7fa',
                padding: '20px 20px 20px 0',
                fontFamily: "'Satoshi', sans-serif",
            }}>
                <Sidebar activeTab={activeTab} setActiveTab={setActiveTab} />
                <Box sx={{
                    flex: 1,
                    overflowY: 'auto',
                    backgroundColor: '#f5f7fa',
                    borderRadius: '10px',
                    fontFamily: "'Satoshi', sans-serif",
                }}>
                    {renderTab()}
                </Box>
            </Box>
        </Box>
    );
};

export default ManageCourses;